import { useEffect, useMemo, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Bug, Camera, Loader2, Monitor, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { useUserStore } from '@/store/useUserStore'
import {
  bugReportEnv,
  clearBufferedApiLogs,
  clearBufferedConsoleErrors,
  clearBufferedInteractions,
  clearBufferedNavigationHistory,
  getBufferedApiLogs,
  getBufferedConsoleErrors,
  getBufferedInteractions,
  getBufferedNavigationHistory,
  getEntryReferrer,
  isBugReportEnabled,
  isBugReportUiVisible,
} from '@/lib/bugReportBuffer'

const BASE_URL = import.meta.env.VITE_API_URL

type Stage = 'idle' | 'capturing' | 'submitting'
type CaptureMode = 'page' | 'screen'

const isMac =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.userAgent)
const SHORTCUT_LABEL = isMac ? '⌃⇧B' : 'Ctrl+Shift+B'

const supportsDisplayMedia =
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices &&
  typeof navigator.mediaDevices.getDisplayMedia === 'function'

const nextFrame = () =>
  new Promise<void>((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  )

/**
 * Tester Bug Reporter — enabled on local/QA/UAT, only when authed. On local/QA
 * it shows a topbar icon; on UAT it's hidden (no icon) and reachable only via
 * the Ctrl+Shift+B hotkey, which is announced in the console at app boot.
 *
 * Capture modes:
 *  - "page"   uses html2canvas-pro on document.body. Silent, fast, but only
 *             sees the SPA's DOM (no browser chrome, no other tabs).
 *  - "screen" uses navigator.mediaDevices.getDisplayMedia. Prompts the user
 *             to pick a screen / window / tab; grabs one video frame from it.
 *
 * Also listens for Ctrl+Shift+B (Cmd+Shift+B on Mac) anywhere in the app —
 * useful when another modal is covering the topbar icon.
 */
export const BugReportLauncher = () => {
  const user = useUserStore((s) => s.user)
  const token = useUserStore((s) => s.token)
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin)
  const selectedWorkspaceId = useUserStore((s) => s.selectedWorkspaceId)

  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [stage, setStage] = useState<Stage>('idle')
  const [captureMode, setCaptureMode] = useState<CaptureMode>('page')
  const [counts, setCounts] = useState({ apiLogs: 0, consoleErrors: 0, navigation: 0, interactions: 0 })
  // While the screen-picker is up we hide the modal so it doesn't leak into
  // the user's tab/window selection or the captured frame. Kept mounted so
  // title + description stay intact if they cancel.
  const [hiddenForCapture, setHiddenForCapture] = useState(false)

  const enabled = isBugReportEnabled() && !!user && !!token
  // On UAT the launcher works but shows no icon — hotkey-only.
  const showButton = enabled && isBugReportUiVisible()

  useEffect(() => {
    if (!enabled) return
    const handler = (e: KeyboardEvent) => {
      const mod = isMac ? e.metaKey : e.ctrlKey
      if (mod && e.shiftKey && (e.key === 'B' || e.key === 'b')) {
        e.preventDefault()
        e.stopPropagation()
        setOpen((prev) => !prev)
      }
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [enabled])

  useEffect(() => {
    if (!open) return
    setCounts({
      apiLogs: getBufferedApiLogs().length,
      consoleErrors: getBufferedConsoleErrors().length,
      navigation: getBufferedNavigationHistory().length,
      interactions: getBufferedInteractions().length,
    })
  }, [open])

  const displayName = useMemo(() => {
    if (!user) return ''
    return isSuperAdmin
      ? user.user?.name
      : user.user_profile?.full_name || user.user?.name
  }, [user, isSuperAdmin])

  if (!enabled) return null

  const close = () => {
    if (stage !== 'idle') return
    setOpen(false)
    setTitle('')
    setDescription('')
    setCaptureMode('page')
  }

  const capturePage = async (): Promise<Blob | null> => {
    const html2canvasMod = await import('html2canvas-pro')
    const html2canvas = html2canvasMod.default
    const canvas = await html2canvas(document.body, {
      logging: false,
      useCORS: true,
      backgroundColor: '#ffffff',
      scale: Math.min(window.devicePixelRatio || 1, 2),
    })
    return new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/png', 0.9)
    )
  }

  const captureScreen = async (): Promise<Blob | null> => {
    // Hide our modal so it doesn't show in the picker preview or capture.
    setHiddenForCapture(true)
    await nextFrame()

    let stream: MediaStream | null = null
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      })

      const video = document.createElement('video')
      video.srcObject = stream
      video.muted = true
      await video.play()
      // Let the first frame render before drawing
      await new Promise<void>((r) => setTimeout(r, 250))

      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) return null
      ctx.drawImage(video, 0, 0)

      return await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png', 0.9)
      )
    } finally {
      stream?.getTracks().forEach((t) => t.stop())
      setHiddenForCapture(false)
    }
  }

  const submit = async () => {
    if (!title.trim()) {
      toast.error('Please add a short title.')
      return
    }

    setStage('capturing')
    let screenshotBlob: Blob | null = null

    try {
      screenshotBlob =
        captureMode === 'screen' && supportsDisplayMedia
          ? await captureScreen()
          : await capturePage()
    } catch (err) {
      // User cancelled the screen picker or capture failed.
      const aborted =
        err instanceof DOMException &&
        (err.name === 'NotAllowedError' || err.name === 'AbortError')
      if (aborted) {
        setStage('idle')
        toast('Screen capture cancelled — report not sent.', { icon: 'ℹ️' })
        return
      }
      console.warn('[BugReport] screenshot failed:', err)
      // Fall through and still send the report without a screenshot.
    }

    setStage('submitting')

    const form = new FormData()
    form.append('title', title.trim())
    if (description.trim()) form.append('description', description.trim())
    form.append('url', window.location.href)
    form.append('route', window.location.pathname)
    form.append('app_env', bugReportEnv())
    form.append('user_agent', navigator.userAgent)
    form.append('viewport', `${window.innerWidth}x${window.innerHeight}`)
    form.append('capture_mode', captureMode)
    form.append('api_logs', JSON.stringify(getBufferedApiLogs()))
    form.append('console_errors', JSON.stringify(getBufferedConsoleErrors()))
    form.append('referrer', getEntryReferrer())
    form.append('navigation_history', JSON.stringify(getBufferedNavigationHistory()))
    form.append('interaction_trail', JSON.stringify(getBufferedInteractions()))
    if (screenshotBlob) {
      form.append('screenshot', screenshotBlob, 'screenshot.png')
    }

    try {
      const headers: HeadersInit = {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      }
      const workspaceId = isSuperAdmin
        ? selectedWorkspaceId
        : user?.workspace_id
      if (workspaceId) {
        ;(headers as Record<string, string>)['X-Workspace-Id'] = String(workspaceId)
      }

      const res = await fetch(`${BASE_URL}/bug-reports`, {
        method: 'POST',
        headers,
        body: form,
      })

      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`)
      }

      toast.success('Bug report sent. Thanks!')
      clearBufferedApiLogs()
      clearBufferedConsoleErrors()
      clearBufferedNavigationHistory()
      clearBufferedInteractions()
      setOpen(false)
      setTitle('')
      setDescription('')
      setCaptureMode('page')
    } catch (err) {
      console.error('[BugReport] submit failed:', err)
      toast.error('Could not send bug report. Please try again.')
    } finally {
      setStage('idle')
    }
  }

  const busy = stage !== 'idle'

  return (
    <>
      {showButton && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-html2canvas-ignore="true"
          title={`Report a bug (${SHORTCUT_LABEL})`}
          aria-label={`Report a bug (${SHORTCUT_LABEL})`}
          className="inline-flex items-center justify-center cursor-pointer bg-transparent border-0 p-0 text-[#3C3295] hover:text-[#FF017E] transition-colors"
        >
          <Bug size={22} />
        </button>
      )}

      {/* Radix Dialog (not a bare portal): its focus scope stacks ABOVE any
          other open modal's trap, so the reporter stays interactive even over
          a focus-trapping Edit modal (QA #55). Outside-click is disabled so a
          stray click can't discard a half-written report. */}
      <Dialog.Root open={open} onOpenChange={(next) => { if (!next) close() }}>
        <Dialog.Portal>
          <Dialog.Overlay
            data-html2canvas-ignore="true"
            className={`fixed inset-0 z-1000 bg-black/50 backdrop-blur-sm animate-in fade-in duration-150 ${
              hiddenForCapture ? 'invisible' : ''
            }`}
          />
          <Dialog.Content
            data-html2canvas-ignore="true"
            aria-describedby={undefined}
            onEscapeKeyDown={(e) => { if (busy) e.preventDefault() }}
            onInteractOutside={(e) => e.preventDefault()}
            className={`fixed left-1/2 top-1/2 z-1000 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-white shadow-xl outline-none animate-in zoom-in-95 duration-150 ${
              hiddenForCapture ? 'invisible' : ''
            }`}
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <div className="flex items-center gap-2">
                <Bug size={18} className="text-[#3C3295]" />
                <Dialog.Title asChild>
                  <h2 className="text-base font-bold text-[#05004E]">Report a bug</h2>
                </Dialog.Title>
                <span className="rounded-full bg-[#F8F9FF] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#3C3295]">
                  {bugReportEnv()}
                </span>
              </div>
              <button
                onClick={close}
                disabled={busy}
                className="cursor-pointer rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
                  Short title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={busy}
                  placeholder="e.g. Save button does nothing on Pillar form"
                  maxLength={255}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#3C3295] focus:ring-2 focus:ring-[#3C3295]/15 disabled:opacity-60"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
                  What were you doing?
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={busy}
                  rows={4}
                  maxLength={5000}
                  placeholder="Steps to reproduce, expected vs actual behavior, anything else useful…"
                  className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#3C3295] focus:ring-2 focus:ring-[#3C3295]/15 disabled:opacity-60"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
                  Screenshot mode
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setCaptureMode('page')}
                    disabled={busy}
                    className={`flex items-start gap-2 rounded-lg border p-3 text-left transition cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 ${
                      captureMode === 'page'
                        ? 'border-[#3C3295] bg-[#F8F9FF] ring-2 ring-[#3C3295]/15'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Camera size={16} className="mt-0.5 shrink-0 text-[#3C3295]" />
                    <div className="text-xs">
                      <div className="font-bold text-[#05004E]">This page</div>
                      <div className="text-slate-500">Silent, automatic</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCaptureMode('screen')}
                    disabled={busy || !supportsDisplayMedia}
                    title={
                      supportsDisplayMedia
                        ? undefined
                        : 'Your browser does not support screen capture'
                    }
                    className={`flex items-start gap-2 rounded-lg border p-3 text-left transition cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 ${
                      captureMode === 'screen'
                        ? 'border-[#3C3295] bg-[#F8F9FF] ring-2 ring-[#3C3295]/15'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Monitor size={16} className="mt-0.5 shrink-0 text-[#3C3295]" />
                    <div className="text-xs">
                      <div className="font-bold text-[#05004E]">Screen / window</div>
                      <div className="text-slate-500">Browser will ask</div>
                    </div>
                  </button>
                </div>
              </div>

              <div className="rounded-lg bg-[#F8F9FF] px-4 py-3 text-xs text-slate-600">
                <div className="mb-1.5 flex items-center gap-1.5 font-bold text-[#05004E]">
                  <Camera size={14} />
                  Will be attached automatically
                </div>
                <ul className="space-y-0.5 pl-5 leading-relaxed">
                  <li>• Screenshot ({captureMode === 'screen' ? 'screen / window of your choice' : 'this page'})</li>
                  <li>• Last {counts.apiLogs} API call{counts.apiLogs === 1 ? '' : 's'}</li>
                  <li>• {counts.consoleErrors} recent console error{counts.consoleErrors === 1 ? '' : 's'}</li>
                  <li>• Last {counts.navigation} page{counts.navigation === 1 ? '' : 's'} visited + {counts.interactions} click{counts.interactions === 1 ? '' : 's'}</li>
                  <li>• URL, viewport, user agent, reporter ({displayName})</li>
                </ul>
                <div className="mt-2 text-[11px] text-slate-500">
                  Tip: press <kbd className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] font-bold text-[#3C3295] shadow-sm">{SHORTCUT_LABEL}</kbd> anywhere to open this dialog.
                </div>
              </div>
            </div>

            <div className="flex gap-2 border-t border-slate-100 px-6 py-4">
              <button
                onClick={close}
                disabled={busy}
                className="flex-1 cursor-pointer rounded-full border border-slate-200 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={busy || !title.trim()}
                className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-full bg-[#3C3295] py-2 text-sm font-bold text-white transition hover:bg-[#2A2266] disabled:opacity-60"
              >
                {stage === 'capturing' && (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Capturing…
                  </>
                )}
                {stage === 'submitting' && (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Sending…
                  </>
                )}
                {stage === 'idle' && 'Send report'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}
